import React, { useCallback, useEffect, useState } from 'react'
import { Button, Heading, Input, LoadingSpinner, Text } from '../components/ui'
import { portaisAtendimentoService } from '../services/portaisAtendimento'
import { logPortaisAtendimentoUpdate } from '../utils/portaisAtendimentoLogs'
import {
  PORTAIS_ATENDIMENTO_PERIODO_DISPONIVEL_COTACAO_KEY,
  parsePeriodoDisponivelCotacao,
  serializePeriodoDisponivelCotacao,
  type HelpdeskParameter,
} from '../types/portaisAtendimento'

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return 'Erro inesperado. Tente novamente.'
}

const PortaisAtendimentoOutrosTab: React.FC = () => {
  const [parameters, setParameters] = useState<HelpdeskParameter[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isHelpdeskAdmin, setIsHelpdeskAdmin] = useState(false)

  const [editingParameter, setEditingParameter] = useState<HelpdeskParameter | null>(null)
  const [editValue, setEditValue] = useState('')
  const [editError, setEditError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const [rows, admin] = await Promise.all([
        portaisAtendimentoService.listOutrosParameters(),
        portaisAtendimentoService.isCurrentUserHelpdeskAdmin(),
      ])
      setParameters(rows)
      setIsHelpdeskAdmin(admin)
    } catch (error) {
      setLoadError(getErrorMessage(error))
      setParameters([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const openEdit = (param: HelpdeskParameter) => {
    if (!isHelpdeskAdmin) return
    setEditingParameter(param)
    setEditValue(param.value ?? '')
    setEditError(null)
  }

  const closeEdit = () => {
    if (saving) return
    setEditingParameter(null)
    setEditValue('')
    setEditError(null)
  }

  const handleSave = async () => {
    if (!editingParameter || !isHelpdeskAdmin) return

    if (editingParameter.key === PORTAIS_ATENDIMENTO_PERIODO_DISPONIVEL_COTACAO_KEY) {
      const months = parsePeriodoDisponivelCotacao(editValue)
      if (months == null) {
        setEditError('Informe um número inteiro de meses maior ou igual a 1.')
        return
      }

      setSaving(true)
      setEditError(null)
      try {
        const oldValue = editingParameter.value
        const serialized = serializePeriodoDisponivelCotacao(months)
        const saved = await portaisAtendimentoService.savePeriodoDisponivelCotacao(months)

        await logPortaisAtendimentoUpdate(
          saved.id,
          'Alterou período disponível de cotação (meses anteriores)',
          { value: oldValue },
          { value: serialized },
          {
            entidade: 'helpdesk_parameters',
            parameter_key: PORTAIS_ATENDIMENTO_PERIODO_DISPONIVEL_COTACAO_KEY,
          }
        )

        await loadData()
        setEditingParameter(null)
        setEditValue('')
      } catch (error) {
        const message = getErrorMessage(error)
        if (message.toLowerCase().includes('permission') || message.toLowerCase().includes('policy')) {
          setEditError('Sem permissão para alterar parâmetros. É necessário ser administrador do helpdesk.')
        } else {
          setEditError(message)
        }
      } finally {
        setSaving(false)
      }
      return
    }

    setEditError('Parâmetro não suportado para edição nesta tela.')
  }

  return (
    <div className="flex-1 flex flex-col overflow-y-auto">
      <div className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Text size="sm" color="muted">
            Outros parâmetros do Portal de Atendimento. Edite o valor conforme necessário.
          </Text>
        </div>

        {!isHelpdeskAdmin && !loading && (
          <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50 dark:bg-amber-950/20 px-6 py-3">
            <Text size="sm" className="text-amber-800 dark:text-amber-200">
              Você pode visualizar esta configuração, mas apenas administradores do helpdesk podem alterá-la.
            </Text>
          </div>
        )}

        {loadError && (
          <div className="mb-4 rounded-lg border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 px-6 py-4">
            <Text size="sm" className="text-red-700 dark:text-red-300">
              {loadError}
            </Text>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg shadow">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/30">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Descrição
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Valor
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {parameters.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400"
                    >
                      Nenhum parâmetro cadastrado. Execute o script SQL de seed no Supabase se ainda não
                      rodou.
                    </td>
                  </tr>
                ) : (
                  parameters.map((param) => (
                    <tr key={param.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td
                        className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 max-w-md truncate"
                        title={param.description ?? undefined}
                      >
                        {param.description ?? '—'}
                      </td>
                      <td
                        className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300"
                        title={param.value ?? undefined}
                      >
                        {param.value?.trim() ? param.value : '—'}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {isHelpdeskAdmin && (
                          <Button variant="outline" size="sm" onClick={() => openEdit(param)}>
                            Editar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingParameter && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={closeEdit} />
            <div
              className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-4 pt-5 pb-4 sm:p-6">
                <Heading as="h3" size="2xl" className="text-gray-900 dark:text-white mb-2">
                  Editar parâmetro
                </Heading>
                <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4 block font-mono">
                  {editingParameter.key}
                </Text>
                <div className="space-y-4">
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Valor (meses) *
                    </Text>
                    <Input
                      type="number"
                      inputMode="numeric"
                      min={1}
                      step={1}
                      value={editValue}
                      onChange={(e) => {
                        setEditValue(e.target.value)
                        setEditError(null)
                      }}
                      className="w-full"
                      disabled={saving}
                      aria-label="Número de meses"
                    />
                    <Text size="xs" className="mt-1 text-gray-500 dark:text-gray-400">
                      Número inteiro de meses anteriores à data atual (≥ 1).
                    </Text>
                  </div>
                  <div>
                    <Text size="sm" className="text-gray-700 dark:text-gray-300 mb-2 block">
                      Descrição
                    </Text>
                    <div className="w-full rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/50 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
                      {editingParameter.description ?? '—'}
                    </div>
                  </div>
                  {editError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-900/20 dark:text-red-200">
                      {editError}
                    </div>
                  )}
                </div>
                <div className="mt-6 flex justify-end gap-2">
                  <Button variant="outline" onClick={closeEdit} disabled={saving}>
                    Cancelar
                  </Button>
                  <Button variant="solid" onClick={() => void handleSave()} disabled={saving}>
                    {saving ? 'Salvando...' : 'Salvar'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PortaisAtendimentoOutrosTab
